/* eslint-disable import-extensions/file-extension-required */
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { defineMessages, useIntl } from 'react-intl';
import { mergeRefs } from 'use-callback-ref';
import { graphql } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import Popup from '@atlaskit/popup';
import { NotificationIndicator } from '@atlaskit/notification-indicator';
import type { NotificationLogProvider } from '@atlaskit/notification-log-client/types';
import { NotificationLogClient } from '@atlaskit/notification-log-client';
import { Notifications } from '@atlaskit/navigation-system/top-nav-items/notifications';

import { GeneralShortcutListener } from '@confluence/shortcuts/entry-points/GeneralShortcutListener.ts';
import { useShortcutStrings } from '@confluence/shortcuts/entry-points/ShortcutVisualizer.ts';
import {
	NOTIFICATIONS_SHORTCUT,
	UNIVERSAL_NOTIFICATIONS_SHORTCUT,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { useRouteDataRef } from '@confluence/route-manager/entry-points/RouteState.ts';
import type { LoadableType } from '@confluence/loadable';
import { LoadableLazy } from '@confluence/loadable';
import { createLazyCallbackHook } from '@confluence/loadable/entry-points/lazy-callback.ts';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore.ts';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance';
import { PERSISTED_KEYS_ON_SERVER } from '@confluence/storage-manager/entry-points/LocalStorageKeys';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';

import { expValEquals } from '@confluence/feature-experiments';
import { useAutoOpenNotificationsEligibility } from './useAutoOpenNotificationsEligibility.ts';
import { useWorkspaceARI } from './useWorkspaceARI.ts';
import { UnsavedNotificationCommentDraftPrompt } from './UnsavedNotificationCommentDraftPrompt.tsx';
import {
	confirmDiscardNotificationCommentDraft,
	handlePopupCloseWithUnsavedChanges,
} from './utils/notificationCommentDraftUtils.ts';
import type {
	NotificationsButtonQuery$data,
	NotificationsButtonQuery$variables,
} from './__generated__/NotificationsButtonQuery.graphql.ts';

const REFRESH_INTERVAL = 60000;
const CLEAR_BADGE_TIME = 10000;
const MAX_NOTIFICATION_COUNT = 9;

export const NotificationsContentLoader: LoadableType<
	Omit<
		{
			flags: FlagsStateContainer;
			onUnsavedCommentDraft?: (hasUnsavedCommentDraft: boolean) => void;
			wasAutoOpened?: boolean;
		},
		'flags'
	>
> = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-NotificationsContent" */ './NotificationsContent.tsx'
			)
		).NotificationsContent,
});

const useLazyClickAnalytics = createLazyCallbackHook(
	async () =>
		(await import(/* webpackChunkName: "loadable-analyticsCallbacks" */ './analyticsCallbacks.ts'))
			.fireNotificationsClickedAnalytics,
);

class EmptyNotificationLogClient implements NotificationLogProvider {
	countUnseenNotifications() {
		return Promise.resolve({ count: 0 });
	}
}

const useNotificationLogClient = (collabContextRoutingAri?: string) => {
	return useMemo(
		() => new NotificationLogClient({ collabContextRoutingAri }),
		[collabContextRoutingAri],
	);
};

const NotificationsButtonQuery = graphql`
	query NotificationsButtonQuery {
		notifications {
			unseenNotificationCount
		}
	}
`;

export const NotificationsButton = (): React.JSX.Element | null => {
	const notificationsButtonRef = useRef<HTMLButtonElement | null>(null);
	const [isOpen, setIsOpen] = useState(false);
	const [isCleared, setIsCleared] = useState(false);
	const wasAutoOpened = useRef(false);
	const { userId, accessStatus } = useSessionData();
	const shouldShowButton =
		accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS ||
		accessStatus === AccessStatus.LICENSED_USE_ACCESS ||
		accessStatus === AccessStatus.EXTERNAL_COLLABORATOR_ACCESS;
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const routeDataRef = useRouteDataRef();
	const intl = useIntl();

	const collabContextRoutingAri = useWorkspaceARI();
	const notificationLogClient = useNotificationLogClient(collabContextRoutingAri);
	const [{ isEditMode: isLiveEditMode }] = useLivePageMode();
	const isOnEditRoute = useIsEditorPage();
	const location = useLocation();
	const [hasUnsavedCommentDraft, setHasUnsavedCommentDraft] = useState(false);

	const { data } = useQueryInterop<
		NotificationsButtonQuery$data,
		NotificationsButtonQuery$variables
	>(NotificationsButtonQuery, {
		interopApolloAggClient: true,
		skip: !userId,
	});

	const [unreadCount, setUnreadCount] = useState(data?.notifications?.unseenNotificationCount || 0);
	const hasNotificationsAutoOpenedBefore =
		localStorage &&
		localStorage.getItem(PERSISTED_KEYS_ON_SERVER.PERSISTED_NOTIFICATIONS_DRAWER_AUTO_OPENED) ===
			'true';
	const isEligibleForExperiment = useAutoOpenNotificationsEligibility();
	const isEligibleForAutoOpen =
		isEligibleForExperiment && unreadCount > 0 && !hasNotificationsAutoOpenedBefore;
	const fireClickedAnalytics = useLazyClickAnalytics(
		createAnalyticsEvent,
		routeDataRef.current?.routeName,
	);

	useEffect(() => {
		if (isCleared) {
			const timeout = setTimeout(() => {
				setIsCleared(false);
			}, CLEAR_BADGE_TIME);

			return () => {
				clearTimeout(timeout);
			};
		}
	}, [isCleared]);

	const onClick = () => {
		if (isOpen) {
			if (hasUnsavedCommentDraft) {
				if (!confirmDiscardNotificationCommentDraft(intl)) {
					return;
				}
			}

			closeNotificationsPopup();
		} else {
			wasAutoOpened.current = false;
			performance.mark('notification.list.render.start');
			setIsCleared(true);
			setIsOpen(true);
		}

		void fireClickedAnalytics();
	};

	const closeNotificationsPopup = useCallback(() => {
		wasAutoOpened.current = false;
		setHasUnsavedCommentDraft(false);
		notificationsButtonRef.current?.focus();
		setIsOpen(false);
		performance.clearMarks('notification.list.render.start');
	}, []);

	const handlePopupCloseEvent = useCallback(
		(event: Event | MouseEvent | KeyboardEvent | null) => {
			if (hasUnsavedCommentDraft) {
				handlePopupCloseWithUnsavedChanges(event, intl, closeNotificationsPopup);
				return;
			}

			closeNotificationsPopup();
		},
		[hasUnsavedCommentDraft, closeNotificationsPopup, intl],
	);

	// Auto-open notifications when eligible and there are unread notifications
	useEffect(() => {
		if (isEligibleForAutoOpen && !isOpen) {
			if (expValEquals('cc-flywheel-auto-open-notifications-exp', 'cohort', 'experiment')) {
				// Preload the lazy-loaded NotificationsContent component, then open
				const preloadAndOpen = async () => {
					await NotificationsContentLoader.preload();
					performance.mark('notification.list.render.start');
					setIsCleared(true);
					setIsOpen(true);
					wasAutoOpened.current = true;
					// Persist to backend so it only auto-opens once
					localStorage.setItem(
						PERSISTED_KEYS_ON_SERVER.PERSISTED_NOTIFICATIONS_DRAWER_AUTO_OPENED,
						'true',
					);
				};
				void preloadAndOpen();
			}
		}
	}, [isEligibleForAutoOpen, isOpen]);

	// Close popup when navigation occurs
	useEffect(() => {
		if (isOpen && location) {
			closeNotificationsPopup();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [closeNotificationsPopup, location]);

	useEffect(() => {
		notificationLogClient
			.countUnseenNotifications()
			.then((res) => {
				setUnreadCount(res.count);
				if (res.count >= 9) {
					createAnalyticsEvent({
						type: 'sendScreenEvent',
						data: {
							name: 'unseenNotificationsOver9',
						},
					}).fire();
				}
			})
			.catch(() => {});
	}, [createAnalyticsEvent, notificationLogClient]);

	const NotificationsBadge = useMemo(
		() => () => {
			return (
				<NotificationIndicator
					onCountUpdated={(count) => {
						setUnreadCount(count.newCount);
					}}
					notificationLogProvider={Promise.resolve(
						isCleared || isOpen ? new EmptyNotificationLogClient() : notificationLogClient,
					)}
					refreshRate={REFRESH_INTERVAL}
					ssrInitialValue={data?.notifications?.unseenNotificationCount}
				/>
			);
		},
		[isOpen, isCleared, notificationLogClient, data?.notifications?.unseenNotificationCount],
	);

	const notificationsLabel =
		unreadCount <= MAX_NOTIFICATION_COUNT
			? intl.formatMessage(i18n.notifications, { count: unreadCount })
			: intl.formatMessage(i18n.overNineNotifications);

	const shortcutStrings = useShortcutStrings({ shortcut: UNIVERSAL_NOTIFICATIONS_SHORTCUT });

	return !shouldShowButton ? null : (
		<>
			{isOpen && hasUnsavedCommentDraft && (
				<UnsavedNotificationCommentDraftPrompt onConfirm={closeNotificationsPopup} />
			)}
			<Popup
				placement="bottom-end"
				shouldFlip
				rootBoundary="viewport"
				content={() => (
					<NotificationsContentLoader
						onUnsavedCommentDraft={setHasUnsavedCommentDraft}
						wasAutoOpened={wasAutoOpened.current}
					/>
				)}
				isOpen={isOpen}
				onClose={handlePopupCloseEvent}
				shouldRenderToParent
				trigger={(triggerProps) => (
					<Notifications
						{...triggerProps}
						badge={NotificationsBadge}
						onClick={onClick}
						isSelected={isOpen}
						label={notificationsLabel}
						shortcut={
							// No shortcut on edit pages
							!isLiveEditMode && !isOnEditRoute ? shortcutStrings : undefined
						}
						ref={mergeRefs([triggerProps.ref, notificationsButtonRef])}
						testId="top-nav-notifications-button"
					/>
				)}
			/>
			<GeneralShortcutListener
				deprecatedAccelerator={
					!(isLiveEditMode || isOnEditRoute) ? NOTIFICATIONS_SHORTCUT : undefined
				}
				accelerator={UNIVERSAL_NOTIFICATIONS_SHORTCUT}
				listener={onClick}
				isAppNavigationShortcut
			/>
		</>
	);
};

const i18n = defineMessages({
	notifications: {
		id: 'app-navigation.top-navigation.notifications',
		defaultMessage:
			'{count, plural, =0{ Notifications} one{{count, number} Notification} other{{count, number} Notifications}}',
		description: 'Button label for the top navigation notifications button.',
	},
	overNineNotifications: {
		id: 'app-navigation.top-navigation.notifications.over-nine-notifications',
		defaultMessage: 'Over 9 Notifications',
		description: 'Label for the notification count when there are more than 9 notifications',
	},
	unsavedChanges: {
		id: 'app-navigation.notifications.unsaved-changes.confirm',
		defaultMessage: 'Do you want to discard this comment? Your comment will be lost if you do.',
		description:
			'Message displayed when there are unsaved changes in the notifications comment editor.',
	},
});
